import { Col, Row } from 'antd';
import { useField, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { findMostRecentVATRate } from '../../../../../shared/getCompanyVATRate';
import * as permissionKind from '../../../../../shared/permissions';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import LanguageSelectField from '../../../../components/fields/LanguageSelectField';
import TimeZoneSelectField from '../../../../components/fields/TimeZoneSelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import { useRuntimeConfig } from '../../../../runtimeConfig';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import MarketTypeField from '../../../shared/MarketTypeField';
import useCountryOptions from '../useCountryOptions';
import useMarketOptions from '../useMarketOptions';
import useCompanyDetailsTooltip from './useCompanyDetailsTooltip';

const CompanyCountryForm = () => {
    const { t } = useTranslation(['companyDetails']);
    const { decimalsOptions } = useSystemOptions();
    const countryOptions = useCountryOptions();

    const { permissions: accountPermissions } = useAccount();
    const company = useCompany(true);
    const { instance } = useRuntimeConfig();

    const hasPermission = company
        ? hasPermissions(company.permissions, [permissionKind.updateCompany]) &&
          hasPermissions(accountPermissions, [permissionKind.updateCompany])
        : hasPermissions(accountPermissions, [permissionKind.updateCompany]);

    const [{ value: countryCode }] = useField('countryCode');
    const [{ value: vatRateSettings }] = useField('vatRateSettings');

    const computeAppliedVATRate = useMemo(() => {
        if (vatRateSettings?.vatRateTable?.length > 0) {
            const found = findMostRecentVATRate(vatRateSettings?.vatRateTable);

            return found?.value;
        }

        return null;
    }, [vatRateSettings?.vatRateTable]);

    // handle UAE
    const { setFieldValue } = useFormikContext();
    const isAE = countryCode === 'AE';
    const marketOptions = useMarketOptions(countryCode);
    useEffect(() => {
        if (!isAE) {
            setFieldValue('marketCode', null);
        }
    }, [isAE, setFieldValue]);

    useEffect(() => {
        setFieldValue('vatRateSettings', {
            ...vatRateSettings,
            appliedVATRate: computeAppliedVATRate,
        });
    }, [computeAppliedVATRate, setFieldValue]);

    const languagesTooltip = useCompanyDetailsTooltip('fields.languages.tooltip');
    const vatRateSettingTooltip = useCompanyDetailsTooltip('fields.VATRateSetting.tooltip');

    return (
        <CollapsibleWrapper defaultActiveKey={t('companyDetails:cardTitles.countryDetails')}>
            <Panel
                key={t('companyDetails:cardTitles.countryDetails')}
                header={t('companyDetails:cardTitles.countryDetails')}
            >
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('companyDetails:fields.countryCode', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="countryCode"
                            options={countryOptions}
                            required
                            showSearch
                        />
                    </Col>
                    {isAE && (
                        <Col lg={8} xs={24}>
                            <FormFields.SelectField
                                {...t('companyDetails:fields.marketCode', { returnObjects: true })}
                                disabled={!hasPermission}
                                name="marketCode"
                                options={marketOptions}
                                required
                                showSearch
                            />
                        </Col>
                    )}
                    <Col lg={8} xs={24}>
                        <LanguageSelectField
                            {...t('companyDetails:fields.languages', { returnObjects: true })}
                            disabled={!hasPermission}
                            mode="multiple"
                            name="languages"
                            shouldFilterByCompany={false}
                            tooltip={languagesTooltip}
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('companyDetails:fields.currency', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="currency"
                            required
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <TimeZoneSelectField
                            {...t('companyDetails:fields.timeZone', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="timeZone"
                            required
                            showSearch
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('companyDetails:fields.roundingAbsoluteAmount', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="roundings.amount.decimals"
                            options={decimalsOptions}
                            required
                            showSearch
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('companyDetails:fields.roundingPercentage', { returnObjects: true })}
                            disabled={!hasPermission}
                            name="roundings.percentage.decimals"
                            options={decimalsOptions}
                            required
                            showSearch
                        />
                    </Col>
                    {instance === 'psme' && (
                        <Col lg={8} xs={24}>
                            <FormFields.VATRateSettingField
                                {...t('companyDetails:fields.VATRateSetting', { returnObjects: true })}
                                disabled={!hasPermission}
                                name="vatRateSettings"
                                tooltip={vatRateSettingTooltip}
                            />
                        </Col>
                    )}
                    <MarketTypeField countryCode={countryCode} disabled={!hasPermission} />
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default CompanyCountryForm;
